import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, XCircle, Copy } from "lucide-react";
import AuthFields from "./TokenInstanceFields";
import { getGroups } from "../../../apis/whatsapp";
import { toast } from "react-toastify";
import CollapsibleJson from "./CollapsibleJson";

const SOCKET_SERVER_URL = process.env.REACT_APP_API_URL;
const API_URL = `${SOCKET_SERVER_URL}/api/whatsapp/get-groups`;

const LANGUAGE_SNIPPETS = ({ token, instanceId }) => {
  const jsonBody = { instanceId: instanceId || "<instanceId>" };
  const jsonStr = JSON.stringify(jsonBody, null, 2);

  return {
    curl: `curl -X POST ${API_URL} \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer ${token || "<token>"}" \\
-d '${jsonStr}'`,

    node: `fetch("${API_URL}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
  },
  body: JSON.stringify(${JSON.stringify(jsonBody, null, 2)})
})`,

    python: `import requests

headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
}

resp = requests.post("${API_URL}", json=${JSON.stringify(jsonBody, null, 2)}, headers=headers)
print(resp.json())`,

    php: `<?php
$data = ${JSON.stringify(jsonBody, null, 2)};

$options = array(
  'http' => array(
    'header'  => "Content-type: application/json\\r\\n" .
                 "Authorization: Bearer ${token || "<token>"}\\r\\n",
    'method'  => 'POST',
    'content' => json_encode($data),
  ),
);
$context  = stream_context_create($options);
$result = file_get_contents('${API_URL}', false, $context);
?>`,

    javascript: `fetch("${API_URL}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
  },
  body: JSON.stringify(${JSON.stringify(jsonBody, null, 2)})
})`,
  };
};

const LANGUAGES = [
  { key: "curl", label: "cURL" },
  { key: "node", label: "Node.js" },
  { key: "python", label: "Python" },
  { key: "php", label: "PHP" },
  { key: "javascript", label: "JavaScript" },
];

const GroupsContent = () => {
  const [inputToken, setInputToken] = useState("");
  const [inputInstanceId, setInputInstanceId] = useState("");
  const [token, setToken] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [editMode, setEditMode] = useState(true);

  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [activeLang, setActiveLang] = useState("curl");
  const [copied, setCopied] = useState(false);
  const snippetRef = useRef(null);

  const [sendResult, setSendResult] = useState(null);

  const handleSave = () => {
    setToken(inputToken);
    setInstanceId(inputInstanceId);
    setEditMode(false);
  };

  const handleReset = () => {
    setInputToken("");
    setInputInstanceId("");
    setToken("");
    setInstanceId("");
    setEditMode(true);
  };

  const handleSend = async () => {
    setSending(true);
    setStatus(null);
    const payload = { instanceId };
    try {
      const { data } = await getGroups(token, payload);

      setSendResult({
        success: data.success,
        message: data.message || (data.success ? "Operation successful." : "Operation failed."),
        data,
      });

      if (data.success) {
        setStatus("success");
        toast.success(data.message || "Operation successful.");
      } else {
        setStatus("error");
        toast.error(data.message || "Operation failed.");
      }
    } catch (error) {
      setStatus("error");
      setSendResult({
        success: false,
        data: error.response?.data || {},
        message: error.response?.data?.message || "Network error occurred.",
      });
      toast.error(error.response?.data?.message || "Network error occurred.");
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    const snippet = LANGUAGE_SNIPPETS({ token, instanceId })[activeLang];

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(snippet).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    } else if (snippetRef.current) {
      const textArea = document.createElement("textarea");
      textArea.value = snippet;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        console.error("Fallback: Oops, unable to copy", err);
      }
      document.body.removeChild(textArea);
    }
  };

  const isSendDisabled = sending || !token.trim() || !instanceId.trim();

  return (
    <div>
      <motion.div
        className="flex flex-col md:flex-row gap-6 p-6 bg-white rounded-xl shadow-lg border border-gray-200 text-gray-900"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, type: "spring" }}
      >
        <div className="w-full">
          {/* Auth fields */}
          <AuthFields
            inputInstanceId={inputInstanceId}
            setInputInstanceId={setInputInstanceId}
            inputToken={inputToken}
            setInputToken={setInputToken}
            instanceId={instanceId}
            token={token}
            editMode={editMode}
            setEditMode={setEditMode}
            onSave={handleSave}
            onReset={handleReset}
          />

          <div className="flex flex-col md:flex-row gap-6">
            {/* Left - Action */}
            <div className="flex-1 max-w-md">
              <h3 className="mb-3 font-semibold text-lg text-gray-900">Get Groups</h3>

              <motion.button
                className={`w-full flex items-center justify-center gap-2 rounded py-2 font-medium mt-1 ${
                  isSendDisabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-[#048AAD] hover:bg-[#048AAD]/90 text-white"
                }`}
                onClick={handleSend}
                disabled={isSendDisabled}
                whileTap={{ scale: isSendDisabled ? 1 : 0.98 }}
                whileHover={isSendDisabled ? {} : { scale: 1.01 }}
                type="button"
              >
                <Send size={20} />
                {sending ? "Fetching..." : "Fetch Groups"}
              </motion.button>

              <div className="h-6 mt-2 flex items-center">
                {status === "success" && (
                  <motion.span className="flex items-center text-[#01CC54] gap-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <CheckCircle size={18} /> Success
                  </motion.span>
                )}
                {status === "error" && (
                  <motion.span className="flex items-center text-red-600 gap-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <XCircle size={18} /> Error
                  </motion.span>
                )}
              </div>
            </div>

            {/* Right - Docs/API Example */}
            <motion.div
              className="flex-2 md:w-3/5 bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-inner flex flex-col"
              style={{ maxHeight: "520px" }}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="mb-2 font-semibold flex-shrink-0 text-gray-900">API Documentation</h4>

              <motion.div
                className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200 shadow-sm text-gray-700 flex items-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                layout
              >
                <div className="flex items-center gap-2 leading-none">
                  <strong className="text-gray-900 break-all font-mono">POST:</strong>
                  <code className="text-[#01CC54] break-all font-mono">{API_URL}</code>
                </div>
              </motion.div>

              {/* Language selector */}
              <div className="mt-3 flex gap-2 mb-2 flex-shrink-0">
                {LANGUAGES.map(({ key, label }) => (
                  <button
                    key={key}
                    className={`px-2 py-1 rounded text-sm font-medium border ${
                      activeLang === key
                        ? "bg-[#048AAD] text-white border-[#048AAD]"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                    }`}
                    onClick={() => setActiveLang(key)}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Code snippet */}
              <div
                ref={snippetRef}
                className="mt-2 p-2 bg-gray-50 rounded mb-2 relative overflow-auto flex-grow border border-gray-200"
                style={{ minHeight: "120px" }}
              >
                <pre className="text-gray-900 text-sm p-3 m-0 whitespace-pre-wrap">
                  {LANGUAGE_SNIPPETS({ token, instanceId })[activeLang]}
                </pre>
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-3 bg-gray-100 hover:bg-gray-200 p-1.5 rounded text-gray-700 z-10 border border-gray-200"
                  title="Copy"
                  type="button"
                >
                  <Copy size={16} />
                </button>
                {copied && (
                  <span className="absolute top-2 right-12 text-xs bg-[#048AAD] text-white px-2 py-0.5 rounded select-none pointer-events-none">
                    Copied!
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <div className="w-full mt-4">
        {sendResult && (
          <div
            className={`mt-4 p-4 rounded-lg border shadow-sm ${
              sendResult.success
                ? "bg-[#01CC54]/10 border-[#01CC54]/30 text-[#01CC54]"
                : "bg-red-50 border-red-300 text-red-700"
            }`}
          >
            <h4 className="font-semibold mb-2 text-gray-900">{sendResult.success ? "Success" : "Error"}</h4>
            <p className="mb-3 whitespace-pre-wrap break-words">{sendResult.message}</p>
            <details className="bg-gray-50 p-2 rounded border border-gray-200 max-h-100 overflow-auto">
              <summary className="cursor-pointer font-mono text-sm mb-2 text-gray-900">
                View Full Response
              </summary>
              <div className="text-xs font-mono max-h-100 overflow-auto text-gray-700">
                <CollapsibleJson data={sendResult.data || {}} />
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupsContent;
